import { z } from 'zod';
import { GRANT_TYPES } from '../constants/oidc.js';
export const authorizeQuerySchema = z.object({
    client_id: z.string().min(1),
    redirect_uri: z.string().url(),
    response_type: z.literal('code'),
    scope: z.string().min(1),
    state: z.string().min(8).max(512),
    code_challenge: z.string().min(43).max(128),
    code_challenge_method: z.literal('S256'),
    nonce: z.string().min(8).max(512).optional(),
    prompt: z.enum(['none', 'login', 'consent', 'select_account']).optional(),
    login_hint: z.string().email().optional(),
});
/**
 * Dossier sur lequel le produit demande à travailler.
 *
 * ── Ce que ce paramètre répare ─────────────────────────────────────────────
 *
 * `id_company` était TOUJOURS le premier dossier de l'espace par date de
 * création. Les gardes des produits (`CompanyGuard` côté Compta,
 * `resolveActiveCompanyId` côté Previ) refusent en 403 tout `x-company-id`
 * différent du claim. Un client possédant plusieurs dossiers pouvait donc en
 * choisir un dans le sélecteur, et n'obtenir ensuite qu'un refus sur chaque
 * appel — le sélecteur montrait des dossiers qu'aucune requête ne pouvait
 * atteindre.
 *
 * ── Pourquoi un paramètre, et pas le cookie ────────────────────────────────
 *
 * Le cookie `semply_active_company` porte déjà la préférence et il est posé
 * sur `.semply.fr`. Mais il est en `SameSite=Lax`, et l'échange de jeton est
 * une requête cross-site (app.semply.fr → api-auth.semply.fr) : le navigateur
 * ne l'enverrait pas. Le relâcher en `SameSite=None` pour ce seul besoin
 * élargirait sa portée bien au-delà de ce qu'il vaut. Le paramètre, lui, est
 * explicite, visible dans la requête, et ne dépend d'aucune politique de
 * cookie.
 *
 * ── Pourquoi il n'ouvre rien ───────────────────────────────────────────────
 *
 * Il ne fait qu'ORIENTER à l'intérieur de ce qui est déjà autorisé :
 * `contextFor` ne le retient que s'il désigne un dossier de l'espace de
 * l'utilisateur, et retombe sinon sur le premier. Un identifiant venu du
 * client ne décide jamais du cloisonnement.
 */
const companyIdParam = z.string().uuid().optional();
/**
 * Secret du client, méthode `client_secret_post` (RFC 6749 §2.3.1).
 *
 * ── Ce que ce champ répare (25/09/2026) ────────────────────────────────────
 *
 * La découverte annonçait `client_secret_post` depuis le premier jour, mais ce
 * schéma ne déclarait pas le champ : Zod retire les clés inconnues, si bien
 * que le secret d'un client confidentiel était jeté avant même d'atteindre le
 * service — et rien, de toute façon, ne lisait `clientSecretHash`. /token et
 * /revoke n'authentifiaient AUCUN client : un `client_id` suffisait.
 *
 * ── Pourquoi optionnel, et sans longueur minimale ──────────────────────────
 *
 * Les clients PUBLICS (tous les produits SEmply aujourd'hui, PKCE) n'ont pas
 * de secret et ne doivent rien voir changer. Certaines bibliothèques envoient
 * pourtant `client_secret=` vide : un `min(1)` ici leur rendrait un 400 pour un
 * champ qui ne les concerne pas. Les bornes (vide = absent, longueur maximale)
 * sont appliquées par l'API, là où l'on sait si le client est confidentiel
 * (`ClientsService.authentifier`).
 */
const clientSecretParam = z.string().optional();
/**
 * Indicateur de ressource, RFC 8707 (27/09/2026, appli mobile).
 *
 * Absent : rien ne change — l'access token est émis pour le client lui-même
 * (`aud` = `client_id`), comme pour tous les produits aujourd'hui.
 *
 * Présent : un URN `urn:semply:api:<client>` qui désigne l'API visée. L'API
 * n'accepte que les audiences que le client a le droit de demander
 * (`OidcClient.allowedAudiences`) : c'est ce qui permet à UN client, l'appli
 * mobile, d'obtenir un jeton par API avec UN seul refresh token — chaque jeton
 * n'ouvrant que son API.
 *
 * UNE seule valeur par requête : un jeton ne porte qu'une audience ici. Un
 * paramètre répété arrive en tableau et est refusé par ce schéma.
 */
const resourceParam = z.string().min(1).max(200).optional();
export const tokenRequestSchema = z.discriminatedUnion('grant_type', [
    z.object({
        grant_type: z.literal(GRANT_TYPES.AUTHORIZATION_CODE),
        client_id: z.string().min(1),
        code: z.string().min(20),
        code_verifier: z.string().min(43).max(128),
        redirect_uri: z.string().url(),
        company_id: companyIdParam,
        client_secret: clientSecretParam,
        resource: resourceParam,
    }),
    z.object({
        grant_type: z.literal(GRANT_TYPES.REFRESH_TOKEN),
        client_id: z.string().min(1),
        refresh_token: z.string().min(20),
        scope: z.string().optional(),
        company_id: companyIdParam,
        client_secret: clientSecretParam,
        resource: resourceParam,
    }),
]);
/**
 * Révocation (RFC 7009) : le client s'y authentifie EXACTEMENT comme à /token
 * — sans quoi n'importe qui connaissant un `client_id` et un refresh token
 * volé pourrait le révoquer au nom d'un client confidentiel, ou sonder quels
 * jetons existent.
 */
export const revokeSchema = z.object({
    token: z.string().min(20),
    token_type_hint: z.enum(['access_token', 'refresh_token']).optional(),
    client_id: z.string().min(1),
    client_secret: clientSecretParam,
});
/**
 * Décision de consentement : le portail renvoie la requête /authorize d'origine,
 * que l'API revalide entièrement (client, redirect_uri, scopes, PKCE) avant de
 * délivrer un code ou de construire la redirection de refus. Le portail ne
 * fabrique jamais d'URL de redirection lui-même : tout passe par l'API, qui
 * seule connaît les redirect_uri enregistrées.
 */
export const consentDecisionSchema = authorizeQuerySchema.extend({
    approved: z.boolean(),
});
export const consentInfoQuerySchema = z.object({
    client_id: z.string().min(1),
    scope: z.string().min(1),
});
/**
 * Adresse d'un produit de la suite (audit du 01/11/2026) : elle finit en lien
 * cliquable et en redirection sur le portail, donc https seulement — jamais
 * `javascript:`, `data:` ou autre schéma. Seule exception : http sur la
 * machine de développement (localhost, 127.0.0.1).
 */
export function urlProduitSure(url) {
    if (!url)
        return false;
    let u;
    try {
        u = new URL(url);
    }
    catch {
        return false;
    }
    if (u.username || u.password)
        return false;
    if (u.protocol === 'https:')
        return true;
    return u.protocol === 'http:' && (u.hostname === 'localhost' || u.hostname === '127.0.0.1');
}
/** Édition d'un produit de la suite depuis le back-office. */
export const updateProductSchema = z
    .object({
    name: z.string().trim().min(1).max(150),
    description: z.string().trim().max(500).nullable(),
    productUrl: z
        .string()
        .trim()
        .max(500)
        .refine(urlProduitSure, { error: 'Adresse en https:// attendue.' })
        .nullable(),
    isLive: z.boolean(),
    displayOrder: z.number().int().min(0).max(999),
})
    .partial();
//# sourceMappingURL=oidc.js.map